import React from 'react';
import merge from 'deepmerge';
import { Content, Dialog, Button } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import { ValueCache } from '@leverege/value-cache';
import { LoadingView } from '@leverege/molten/views';
import { differenceInHours } from 'date-fns';
import S from './ReplaceCustomerLocator.css';
import ConfirmLocatorPresent from './replaceLocator/ConfirmLocatorPresent';
import SalesFlowConstants from '../salesFlow/Constants';
import SelectLocator from './replaceLocator/SelectLocator';
import RecovrSales from '../../util/RecovrSales';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default class TransferCustomerLocator extends React.Component {
  static defaultProps = {
    headerVariant: 'replaceCustomerLocatorHeader|formHeader',
    contentVariant: 'replaceCustomerLocatorContent|dialogContent',
    footerVariant: 'replaceCustomerLocatorFooter|dialogFooter',
    icon: 'fa fa-globe',
    title: 'Replace Customer Locator'
  };
  constructor(props) {
    super(props);
    this.flowCache = new ValueCache();
    this.state = {
      step: 0,
      locatorPresent: false,
      isSearching: false,
      isScanning: false,
      locatorId: null,
      selectedLocator: null,
      locatorError: null,
      replacing: false
    };
  }
  onChange = ({
    value,
    data
  }) => {
    this.setState({
      [data]: value
    });
  };
  onSearchChange = ({
    value,
    object
  }) => {
    const locatorError = this.getLocatorError(object);
    this.setState({
      isSearching: false,
      locatorId: value,
      selectedLocator: object,
      locatorError
    });
  };
  onScanChange = ({
    value,
    object
  }) => {
    const locatorError = this.getLocatorError(object);
    this.setState({
      isScanning: false,
      locatorId: value,
      selectedLocator: object,
      locatorError
    });
  };
  onReplaceLocator = async () => {
    const {
      sale,
      actions,
      onClose
    } = this.props;
    const {
      selectedLocator
    } = this.state;
    this.setState({
      replacing: true
    });
    await RecovrSales.replaceLocator({
      sale,
      newLocatorId: selectedLocator.id,
      actions,
      onClose
    }).catch(() => {
      this.setState({
        replacing: false
      });
    });
    this.setState({
      replacing: false
    });
  };
  getLocatorError(locator) {
    const batteryLevel = locator.data?.batteryLevel;
    if (!batteryLevel || batteryLevel < 70) {
      return 'lowBattery';
    }
    const lastHeartbeat = locator.data?.lastHeartbeat;
    if (differenceInHours(Date.now(), lastHeartbeat) >= 3) {
      return 'offline';
    }
    const keystreamDeviceState = locator.data?.keystreamDeviceState;
    if (keystreamDeviceState !== SalesFlowConstants.KEYSTREAM_ACTIVATED) {
      return 'unactivated';
    }
    return null;
  }
  getFlow = () => {
    const {
      title,
      icon,
      onClose,
      headerVariant,
      contentVariant,
      footerVariant
    } = this.props;
    const {
      locatorPresent,
      isSearching,
      isScanning,
      locatorError,
      replacing
    } = this.state;
    return this.flowCache.calc(props => {
      return {
        defaults: {
          header: {
            render: () => {
              return /*#__PURE__*/_jsxs(React.Fragment, {
                children: [/*#__PURE__*/_jsx(TitleBar, {
                  variant: "replaceCustomerLocatorDialogTitle|dialogTitle",
                  title: title,
                  icon: icon
                }), /*#__PURE__*/_jsx(Button, {
                  variant: "closeDialog",
                  className: S.close,
                  onClick: onClose
                })]
              });
            },
            variant: headerVariant
          },
          content: {
            variant: contentVariant,
            layout: 'flex:colM'
          },
          footer: {
            variant: footerVariant,
            layout: 'flex:rowMEnd'
          }
        },
        steps: [{
          id: 'confirmLocatorPresent',
          content: {
            component: ConfirmLocatorPresent,
            props: {
              onChange: this.onChange
            }
          },
          footer: {
            render: () => {
              return /*#__PURE__*/_jsxs(React.Fragment, {
                children: [/*#__PURE__*/_jsx(Button, {
                  variant: "primaryCancel",
                  onClick: onClose,
                  children: "Cancel"
                }), /*#__PURE__*/_jsx(Button, {
                  variant: "primary",
                  disabled: !locatorPresent,
                  onClick: () => {
                    const {
                      step
                    } = this.state;
                    this.setState({
                      step: step + 1
                    });
                  },
                  children: "Next"
                })]
              });
            }
          }
        }, {
          id: 'selectLocator',
          content: {
            component: SelectLocator,
            props: {
              onSearchChange: this.onSearchChange,
              onScanChange: this.onScanChange,
              onChange: this.onChange
            }
          },
          footer: {
            render: () => {
              if (isSearching || isScanning) {
                return null;
              }
              return /*#__PURE__*/_jsxs(React.Fragment, {
                children: [/*#__PURE__*/_jsx(Button, {
                  variant: "primaryCancel",
                  onClick: onClose,
                  children: "Cancel"
                }), /*#__PURE__*/_jsx(Button, {
                  variant: "primary",
                  disabled: !locatorPresent || locatorError || replacing,
                  onClick: this.onReplaceLocator,
                  children: replacing ? /*#__PURE__*/_jsx(LoadingView.Inline, {}) : 'Replace Locator'
                })]
              });
            }
          }
        }]
      };
    }, this.props, this.state);
  };
  getStepData = () => {
    const {
      step
    } = this.state;
    const flow = this.getFlow();
    return merge(flow.defaults, flow.steps[step], {
      arrayMerge: overwriteMerge
    });
  };
  renderStep(step) {
    const stepData = this.getStepData();
    const {
      header,
      content,
      footer
    } = stepData;
    return /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx(Content.Header, {
        variant: header.variant,
        layout: header.layout,
        children: renderStepSection(header, this.props, this.state)
      }), /*#__PURE__*/_jsx(Content.Area, {
        variant: content.variant,
        layout: content.layout,
        children: renderStepSection(content, this.props, this.state)
      }), /*#__PURE__*/_jsx(Content.Footer, {
        variant: footer.variant,
        layout: footer.layout,
        children: renderStepSection(footer, this.props, this.state)
      })]
    });
  }
  render() {
    const {
      show,
      onClose
    } = this.props;
    const {
      step
    } = this.state;
    return /*#__PURE__*/_jsx(Dialog, {
      variant: "replaceCustomerLocator",
      show: show,
      onClose: onClose,
      children: /*#__PURE__*/_jsx("form", {
        onSubmit: e => e.preventDefault(),
        children: /*#__PURE__*/_jsx(Content, {
          relative: true,
          children: this.renderStep(step)
        })
      })
    });
  }
}
function renderStepSection(section, props = {}, formState = {}) {
  const {
    component: Component,
    render
  } = section;
  if (Component) {
    return /*#__PURE__*/_jsx(Component, {
      ...props,
      ...section.props,
      formState: formState
    });
  }
  if (render) {
    return render({
      ...section.props,
      ...props,
      formState
    });
  }
  return null;
}
function overwriteMerge(dest, source) {
  return source;
}